import React, { Fragment, useEffect, useState, useMemo, useCallback } from 'react';
import PageTemplate from 'app/components/templates/PageTemplate';
import {
    Button,
    List,
    ListItem,
    ListItemText,
    ListSubheader,
    MdiIcon,
    TextField,
    Divider,
} from '@mic3/platform-ui';
import { useDispatch, useSelector } from 'react-redux';

import { addEventDataAttribute, loadEventDataAttributes, removeEventDataAttribute } from 'store/actions/stream/eventsActions';
import AddEventTypeAttribute, { fieldTypesOptions } from './AddEventTypeAttribute';
import { openEventTypeAttributeSidebar } from 'store/actions/entities/eventTypeAttributeSidebarActions';
import HeaderBar from 'app/components/molecules/HeaderBar/HeaderBar';
import Breadcrumbs from 'app/components/organisms/Breadcrumbs/Breadcrumbs';
import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import styled from 'styled-components';
import NestedMenu from 'app/components/molecules/NestedMenu/NestedMenu';
import { useParams } from 'react-router';
import { gql, useQuery } from '@apollo/client';
import { Chip } from '@material-ui/core';
import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import Loader from 'app/components/atoms/Loader/Loader';
import { attributeIconSelect, buildAttributeMenu, getAttributeFieldType } from './eventTypesUtils';
import withPermissions from 'app/utils/withPermissions';

const StyledListItem = styled(ListItem)(({ theme, isSelected }) => ({
    backgroundColor: isSelected
        ? theme.material.colors.itemActive
        : 'transparent',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: isSelected ? theme.material.colors.itemActive : theme.material.colors.background.hover,
    },
}));

const TextFieldStyled = styled(TextField)`
    flex-grow: 1;
    margin: 4px 0 4px 0px !important;
    max-width: ${({ mobile }) => (mobile ? '100%' : '215px')};
    &.MuiFormControl-fullWidth {
        min-width: ${({ mobile }) => !mobile && '125px !important;'};
    }
`;

const DividerStyled = styled(Divider)`
    margin: 5px 0 16px 0 !important;
`;

const StyledNestedMenu = styled(NestedMenu)`
    i.mdi-circle-slice-8:before,
`;

const ChipStyled = styled(Chip)`
    background-color: ${({ theme }) => theme.material.colors.background.activeElement} !important;
    color: ${({ theme }) => theme.material.colors.text.button} !important;
    text-transform: uppercase;
    font-size: 14px !important;
`;

const EVENT_TYPE_DETAILS_QUERY = gql`
query loadEventTypeDetailsQuery($id: ID!) {
    result: eventType(id: $id) {
        id
        name
    }
}
`;

const ContentAreaStyled = styled(ContentArea)`
    padding: 0 1rem 1rem 1rem !important;
    .MuiListSubheader-sticky {
        top: 0px !important;
        background-color: ${({ theme }) => theme.material.colors.background.default} !important;
    }
`;

const StickyHeader = styled.div`
    position: sticky;
    top: 0px;
    z-index: 100;
    padding: 0 2rem;
    .MuiGrid-container{
        margin-left: -10px !important;
    }
`;

const HeaderBarStyled = styled(HeaderBar)`
    margin: 0  !important;

`;

const ButtonStyled = styled(Button)`
    margin: 0 1rem;
`;

const EventTypeAttributes = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const details = useSelector(state => state.stream.events.attributesData?.data);
    const isSidebarOpen = useSelector(state => state.sidebar?.isOpen);
    const eventDataAttributes = details?.eventDataAttributes;

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [mousePosition, setMousePosition] = useState(null);
    const [openAttributeModal, setOpenAttributeModal] = useState(false);
    const [selectedAttributeType, setSelectedAttributeType] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const { data } = useQuery(EVENT_TYPE_DETAILS_QUERY, { variables: { id }, fetchPolicy: 'network-only' });
    const filterBy = useMemo(() => ({
        field: 'eventType',
        op: '=',
        value: id
    }),[id])

    useEffect(() => {
        if(!isSidebarOpen && selectedId){
            setSelectedId(null)
        }
    }, [isSidebarOpen, selectedId]);

    useEffect(() => {
        setLoading(true)
        dispatch(loadEventDataAttributes({ filterBy })).finally(() => setLoading(false));
    }, [dispatch, id, filterBy]);

    const buildBreadcrumbs = useMemo(() => {
        return (
            <Breadcrumbs
                list={[
                    { link: `/events/event-types`, title: 'Event Type' },
                    { title: data?.result?.name || '' },
                ]}
                withGoBack
            />
        );
    }, [data?.result?.name]);

    const handleAttributeClick = useCallback((attribute) => {
        const { id, label, format, type } = attribute || {};
        if(id === selectedId && isSidebarOpen) return;
        setSelectedId(id)
        dispatch(
            openEventTypeAttributeSidebar({
                title: label,
                subTitle: getAttributeType(attribute),
                attributeId: id,
                attributeIcon: attributeIconSelect(format, type),
            })
        );
    }, [dispatch, selectedId, isSidebarOpen]);

    const getAttributeType = (att) => {
        if (!att?.format) return '';
        return att.format === 'none' ? 'Text Field' : att.format;
    };

    const handleAddAttributeClick = (label) => {
        const selectedAttributeType = fieldTypesOptions.find(f => f.label === label).value;
        setMousePosition(null);
        setOpenAttributeModal(true);
        setSelectedAttributeType(selectedAttributeType);
    };

    const attributesToDisplay = useMemo(() => {
        return searchTerm
            ? eventDataAttributes.filter(attribute =>
                (attribute.label || `Attribute ${0}`)
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            )
            : eventDataAttributes;
    }, [searchTerm, eventDataAttributes]);

    const handleCopyAttribute = useCallback((attribute) => {
        const {
            id,
            indexName,
            state,
            label,
            attribute: _attribute,
            ...rest
        } = attribute || {};
        const copiedData = {
            ...rest,
            label: label + ' (copy)',
            attribute: _attribute + ' (copy)'
        };
        setLoading(true)
        dispatch(addEventDataAttribute(copiedData)).then(result => {
            if (!(result instanceof Error) && result.id) {
                dispatch(loadEventDataAttributes({ filterBy }));
            }
        }).finally(() => setLoading(false));
    },[filterBy, dispatch])

    const handleDeleteAttribute = useCallback((attribute) => {
        setLoading(true)
        dispatch(removeEventDataAttribute(attribute?.id)).then(res => {
            if (!(res instanceof Error)) {
                dispatch(loadEventDataAttributes({ filterBy }));
            }
        }).finally(() => setLoading(false));
    },[dispatch, filterBy])

    const handleDotMenuClick = useCallback((title, attribute) => {
        switch(title){
            case 'Copy':
                handleCopyAttribute(attribute);
                break;
            case 'Delete attribute':
                handleDeleteAttribute(attribute)
                break;
            default:
                return;

        }
    },[handleCopyAttribute, handleDeleteAttribute])

    return (
        <Fragment>
            <PageTemplate title="Attributes">
                {loading && <Loader absolute />}
                <StickyHeader>
                    <HeaderBarStyled left={buildBreadcrumbs} />
                    <TextFieldStyled
                        variant="standard"
                        margin="none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth
                        placeholder="Search..."
                    />
                    <DividerStyled />
                    <ButtonStyled
                        variant="contained"
                        color="primary"
                        onClick={(e) =>
                            setMousePosition({
                                top: e.clientY,
                                left: e.clientX,
                            })
                        }
                        fullWidth
                    >
                        <MdiIcon id="add_new_attribute_icon" name="plus" />
                            Add New
                    </ButtonStyled>
                    <StyledNestedMenu
                        items={fieldTypesOptions}
                        open={Boolean(mousePosition)}
                        onItemClick={handleAddAttributeClick}
                        anchorReference="anchorPosition"
                        anchorPosition={mousePosition || { top: 0, left: 0 }}
                        onClose={() => setMousePosition(null)}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    />
                </StickyHeader>
                <ContentAreaStyled isSidebar={false}>
                    
                    <List
                        subheader={
                            <ListSubheader component="div" id="nested-list-subheader">
                                    Attribute name A-Z ({eventDataAttributes?.length || 0})
                            </ListSubheader>
                        }
                    >
                        {attributesToDisplay?.map((attribute, index) => (
                            <StyledListItem
                                key={index}
                                isSelected={selectedId === attribute?.id}
                                onClick={() => handleAttributeClick(attribute)}
                            >
                                <MdiIcon
                                    name={attributeIconSelect(attribute?.format, attribute?.type)}
                                    style={{ marginRight: '16px' }}
                                />
                                <ListItemText
                                    primary={attribute.label || `Attribute ${0}`}
                                    secondary={getAttributeFieldType(attribute)}
                                />
                                {attribute?.state === 'created' && (
                                    <ChipStyled
                                        size="large"
                                        label={'INDEXED'}
                                        clickable
                                    />
                                )}
                                <DotMenu
                                    onItemClick={(title) => handleDotMenuClick(title, attribute)}
                                    items={buildAttributeMenu(attribute?.state === 'created')}
                                />
                            </StyledListItem>
                        ))}
                    </List>
                    {!attributesToDisplay?.length && !loading && (
                        <div style={{ padding: '1rem', color: '#888', margin: 'auto', width: '100%' }}>
                            <div>No results</div>
                        </div>
                    )}
                </ContentAreaStyled>
                {openAttributeModal && (
                    <AddEventTypeAttribute
                        selectedAttribute={{ format: selectedAttributeType }}
                        eventTypeId={id}
                        onClose={() => setOpenAttributeModal(false)}
                    />
                )}
            </PageTemplate>
        </Fragment>
    );
};

export default withPermissions(EventTypeAttributes, 'eventtype');
